import { io } from "socket.io-client";
import { baseURL } from "../config/index";
import {
    getAuthenticationSession,
    subscribeToAuthentication,
} from "./authSession";

const getCurrentAccessToken = (session = getAuthenticationSession()) => {
    if (session?.status !== "authenticated") return null;

    const token = typeof session.token === "string" ? session.token.trim() : "";
    return token || null;
};

const socket = io(baseURL, {
    autoConnect: false,
    transports: ['websocket'],
    auth: (callback) => {
        const token = getCurrentAccessToken();
        callback(token ? { token } : {});
    },
});

const pendingRoomJoins = new Set();

const cancelPendingRoomJoins = () => {
    for (const cancelJoin of Array.from(pendingRoomJoins)) {
        cancelJoin();
    }
};

export const connectSocket = () => {
    if (socket.connected || socket.active) return true;

    const session = getAuthenticationSession();
    if (!getCurrentAccessToken(session)) {
        if (session?.status === "anonymous") {
            cancelPendingRoomJoins();
        }
        if (session?.status !== "loading") {
            console.error("Cannot connect socket: No authenticated session");
        }
        return false;
    }

    socket.connect();
    return true;
};

export const disconnectSocket = () => {
    cancelPendingRoomJoins();
    if (socket.connected || socket.active) {
        socket.disconnect();
    }
};

export const joinTrackingRoom = (visitID) => {
    if (!visitID) return () => {};

    let isCancelled = false;
    const cancelJoin = () => {
        if (isCancelled) return;
        isCancelled = true;
        pendingRoomJoins.delete(cancelJoin);
        socket.off("connect", emitJoin);
    };
    const emitJoin = () => {
        if (isCancelled) return;
        pendingRoomJoins.delete(cancelJoin);
        socket.off("connect", emitJoin);
        console.log("Emitting join_visit_tracking for:", visitID);
        socket.emit("join_visit_tracking", visitID);
    };

    if (socket.connected) {
        emitJoin();
    } else {
        pendingRoomJoins.add(cancelJoin);
        socket.once("connect", emitJoin);
        connectSocket();
    }

    return cancelJoin;
};

export const leaveTrackingRoom = (visitID) => {
    if (!visitID || !socket.connected) return;
    socket.emit("leave_visit_tracking", visitID);
};

export const onLocationUpdate = (callback) => {
    // Listen for the broadcasted event from server
    socket.on("location_update", callback);
    return () => socket.off("location_update", callback);
};

export const offLocationUpdate = (callback) => {
    if (callback) socket.off("location_update", callback);
    else socket.off("location_update");
};

export const onStatusUpdate = (callback) => {
    socket.on("status_update", callback);
    return () => socket.off("status_update", callback);
};

export const offStatusUpdate = (callback) => {
    if (callback) socket.off("status_update", callback);
    else socket.off("status_update");
};

subscribeToAuthentication(() => {
    const session = getAuthenticationSession();
    const hasAuthenticatedSession = Boolean(getCurrentAccessToken(session));

    if (hasAuthenticatedSession) {
        if (pendingRoomJoins.size > 0 && !socket.connected && !socket.active) {
            socket.connect();
        }
        return;
    }

    if (session?.status === "anonymous") {
        disconnectSocket();
    }
});

export default socket;
